// src/Catalogo.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ProductoConDetalles } from './types';
import AnimationWrapper from './AnimationWrapper';
import { MARCAS_CARRO, MARCAS_MOTO, getModelosPorMarca, MOCK_PRODUCTOS } from './data/productos';
import { FiltrosBusqueda } from './HeroSearchFilter';
import ShareButton from './ShareButton';

// ... (filterProducts se mantiene IGUAL) ...
const filterProducts = (productos: ProductoConDetalles[], filtros: FiltrosBusqueda): ProductoConDetalles[] => {
    let productosFiltrados = productos;
    if (filtros.categoria === 'vehiculo') {
        productosFiltrados = productosFiltrados.filter(p => p.tipo === 'carro' || p.tipo === 'moto');
        if (filtros.tipoVehiculo !== 'todos') productosFiltrados = productosFiltrados.filter(p => p.tipo === filtros.tipoVehiculo);
        if (filtros.condicion !== 'todos') productosFiltrados = productosFiltrados.filter(p => (p.condicion || 'nuevo') === filtros.condicion);
        if (filtros.marca) productosFiltrados = productosFiltrados.filter(p => p.nombre.toLowerCase().includes(filtros.marca.toLowerCase()));
        if (filtros.modelo) productosFiltrados = productosFiltrados.filter(p => p.nombre.toLowerCase().includes(filtros.modelo.toLowerCase()));
    } else if (filtros.categoria === 'repuestos') {
        productosFiltrados = productosFiltrados.filter(p => p.tipo === 'repuesto' || p.tipo === 'accesorio');
        if (filtros.queryTexto) productosFiltrados = productosFiltrados.filter(p => p.nombre.toLowerCase().includes(filtros.queryTexto.toLowerCase()) || p.descripcion.toLowerCase().includes(filtros.queryTexto.toLowerCase()));
    }
    return productosFiltrados;
};

const ListaItem: React.FC<{ producto: ProductoConDetalles }> = ({ producto }) => {
    const isVehiculo = producto.tipo === 'carro' || producto.tipo === 'moto';
    const badgeColor = producto.tipo === 'moto' ? 'bg-red-600' : 'bg-yellow-600';
    const buttonColor = producto.tipo === 'moto' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700';

    return (
        <Link 
            to={`/detalle/${producto.id}`}
            className="flex flex-col md:flex-row bg-white shadow-lg overflow-hidden mb-6 transform transition duration-300 hover:shadow-2xl hover:-translate-y-1 border border-gray-100 group cursor-pointer relative"
        >
            <div className="md:w-72 h-64 md:h-auto overflow-hidden relative flex-shrink-0 bg-gray-200">
                <img src={producto.imgUrl} alt={producto.nombre} loading="lazy" className="w-full h-full object-cover transition duration-700 group-hover:scale-110"/>
                <span className={`absolute top-3 left-3 ${badgeColor} text-white text-xs font-extrabold px-3 py-1 shadow-md uppercase tracking-wide`}>{producto.tipo.toUpperCase()}</span>
                {producto.destacado && <span className="absolute top-3 right-3 bg-black text-yellow-500 text-xs font-extrabold px-2 py-1 shadow-md uppercase tracking-wide border border-yellow-500">Destacado</span>}
            </div>
            <div className="p-6 flex flex-col justify-between flex-grow relative">
                
                {/* 2. BOTÓN DE COMPARTIR EN LA TARJETA (Absoluto arriba derecha) */}
                <div className="absolute top-4 right-4 z-10">
                    <ShareButton producto={producto} className="shadow-sm border border-gray-100 bg-white hover:bg-gray-50" />
                </div>

                <div>
                    <div className='flex justify-between items-start mb-2 pr-10'> {/* pr-10 para no tapar con el botón */}
                        <h3 className="text-2xl font-extrabold text-gray-900 group-hover:text-yellow-600 transition-colors">{producto.nombre}</h3>
                        <span className={`text-2xl font-bold ${isVehiculo && producto.tipo === 'moto' ? 'text-red-600' : 'text-yellow-600'} hidden md:block`}>
                            ${producto.precio.toLocaleString('es-ES')}
                        </span>
                    </div>
                    {/* Precio visible en móvil también */}
                    <span className={`text-2xl font-bold ${isVehiculo && producto.tipo === 'moto' ? 'text-red-600' : 'text-yellow-600'} block md:hidden mb-2`}>
                        ${producto.precio.toLocaleString('es-ES')}
                    </span>
                    
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2 leading-relaxed">{producto.descripcion}</p>
                </div>
                <div className="flex justify-end mt-2">
                    <span className={`text-white font-bold py-3 px-8 shadow-md transition duration-200 text-sm uppercase tracking-wider ${buttonColor}`}>Ver Detalles</span>
                </div>
            </div>
        </Link>
    );
};

const ProductCatalog: React.FC = () => {
    // ... (El resto del componente ProductCatalog SE MANTIENE EXACTAMENTE IGUAL)
    const location = useLocation();
    const initialFiltros: FiltrosBusqueda = {
        categoria: 'vehiculo', tipoVehiculo: 'todos', marca: '', modelo: '', condicion: 'todos', queryTexto: '', 
    };
    const [currentFiltros, setCurrentFiltros] = useState<FiltrosBusqueda>(initialFiltros);
    const [visibleCount, setVisibleCount] = useState(6);
    const [showFiltersMobile, setShowFiltersMobile] = useState(false);

    useEffect(() => {
        if (location.state && location.state.filtrosIniciales) {
            setCurrentFiltros(location.state.filtrosIniciales);
        }
    }, [location.state]);

    const productosFiltradosTotal = useMemo(() => {
        return filterProducts(MOCK_PRODUCTOS, currentFiltros);
    }, [currentFiltros]);

    const productosVisibles = productosFiltradosTotal.slice(0, visibleCount);
    const hayMasProductos = productosFiltradosTotal.length > visibleCount;

    const handleSideFilterChange = (key: keyof FiltrosBusqueda, value: any) => {
        setCurrentFiltros(prev => ({
            ...prev,
            [key]: value,
            ...(key === 'tipoVehiculo' && { marca: '', modelo: '' }), 
        }));
    };

    const inputClass = "w-full p-3 bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 block transition-all duration-200 outline-none";
    const labelClass = "block mb-2 text-sm font-bold text-gray-700 uppercase tracking-wide";

    return (
        <div className="bg-gray-50 min-h-screen pt-28 pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimationWrapper direction='up'>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-4 uppercase tracking-tighter">
                            CATÁLOGO <span className="text-yellow-600">BESTUNE</span>
                        </h1>
                        <div className="h-1 w-24 bg-yellow-600 mx-auto mb-4"></div>
                        <p className="text-lg text-gray-500 max-w-2xl mx-auto">Encuentra tu próximo vehículo nuevo o usado.</p>
                    </div>
                </AnimationWrapper>

                <div className="lg:hidden mb-6">
                    <button onClick={() => setShowFiltersMobile(!showFiltersMobile)} className="w-full bg-black text-white py-3 px-4 flex justify-between items-center font-bold shadow-lg">
                        <span className="flex items-center">
                            <svg className="w-5 h-5 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
                            {showFiltersMobile ? 'Ocultar Filtros' : 'Mostrar Filtros Avanzados'}
                        </span>
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <div className={`lg:col-span-1 ${showFiltersMobile ? 'block' : 'hidden lg:block'}`}>
                        <AnimationWrapper direction='left'>
                            <div className="sticky top-28 shadow-xl overflow-hidden border border-gray-200 bg-white">
                                <div className="bg-black p-5 border-b-4 border-yellow-600 flex items-center">
                                    <svg className="w-6 h-6 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
                                    <h3 className="text-xl font-bold text-white uppercase tracking-wider">Filtros</h3>
                                </div>
                                <div className="p-6 space-y-6">
                                    <div>
                                        <label className={labelClass}>Categoría</label>
                                        <select value={currentFiltros.categoria} onChange={(e) => handleSideFilterChange('categoria', e.target.value)} className={inputClass}>
                                            <option value="vehiculo">Vehículos</option>
                                            <option value="repuestos">Repuestos y Accesorios</option>
                                        </select>
                                    </div>
                                    {currentFiltros.categoria === 'vehiculo' && (
                                        <>
                                            <div>
                                                <label className={labelClass}>Condición</label>
                                                <select value={currentFiltros.condicion} onChange={(e) => handleSideFilterChange('condicion', e.target.value)} className={inputClass}>
                                                    <option value="todos">Todos</option>
                                                    <option value="nuevo">Nuevos</option>
                                                    <option value="usado">Usados</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className={labelClass}>Tipo</label>
                                                <select value={currentFiltros.tipoVehiculo} onChange={(e) => handleSideFilterChange('tipoVehiculo', e.target.value)} className={inputClass}>
                                                    <option value="todos">Todos</option>
                                                    <option value="carro">Carros</option>
                                                    <option value="moto">Motos</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className={labelClass}>Marca</label>
                                                <select value={currentFiltros.marca || 'todas'} onChange={(e) => handleSideFilterChange('marca', e.target.value === 'todas' ? '' : e.target.value)} className={inputClass}>
                                                    <option value="todas">Todas las Marcas</option>
                                                    {(currentFiltros.tipoVehiculo === 'carro' || currentFiltros.tipoVehiculo === 'todos' ? MARCAS_CARRO : []).map(m => <option key={m} value={m}>{m} (Carro)</option>)}
                                                     {(currentFiltros.tipoVehiculo === 'moto' || currentFiltros.tipoVehiculo === 'todos' ? MARCAS_MOTO : []).map(m => <option key={m} value={m}>{m} (Moto)</option>)}
                                                </select>
                                            </div>
                                             {(currentFiltros.marca) && (
                                                <div className="animate-fadeIn">
                                                    <label className={labelClass}>Modelo</label>
                                                    <select value={currentFiltros.modelo || 'todos'} onChange={(e) => handleSideFilterChange('modelo', e.target.value === 'todos' ? '' : e.target.value)} className={inputClass}>
                                                        <option value="todos">Todos los Modelos</option>
                                                        {getModelosPorMarca(currentFiltros.marca).map(m => <option key={m} value={m}>{m}</option>)}
                                                    </select>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {currentFiltros.categoria === 'repuestos' && (
                                        <div className="animate-fadeIn">
                                            <label className={labelClass}>Buscar Pieza</label>
                                            <input type="text" placeholder="Ej: Batería..." value={currentFiltros.queryTexto} onChange={(e) => handleSideFilterChange('queryTexto', e.target.value)} className={inputClass}/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </AnimationWrapper>
                    </div>

                    <div className="lg:col-span-3">
                        <AnimationWrapper direction='right'>
                            <div className="flex justify-between items-center mb-6 bg-white p-4 shadow-sm border border-gray-100">
                                <p className="text-gray-600 font-medium">Mostrando <span className="font-bold text-black">{productosFiltradosTotal.length}</span> resultados</p>
                            </div>
                            <div className="space-y-6">
                                {productosVisibles.length > 0 ? (
                                    productosVisibles.map((producto, index) => (
                                        <AnimationWrapper key={producto.id} direction='up' delay={index * 50}>
                                            <ListaItem producto={producto} />
                                        </AnimationWrapper>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16 bg-white shadow-inner border border-dashed border-gray-300">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">No encontramos coincidencias</h3>
                                        <button onClick={() => setCurrentFiltros(initialFiltros)} className="mt-6 text-yellow-600 font-bold hover:underline">Limpiar filtros</button>
                                    </div>
                                )}
                            </div>
                        </AnimationWrapper>
                        {hayMasProductos && (
                            <div className="text-center mt-12">
                                <button onClick={() => setVisibleCount(prev => prev + 6)} className="bg-black text-white font-bold py-4 px-10 hover:bg-yellow-600 transition duration-300 text-lg shadow-lg">Cargar Más</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>    
    );
};

export default ProductCatalog;